import { useState, useCallback, useEffect } from 'react';
import { Table, Button } from 'reactstrap';
import { BiPlus } from 'react-icons/bi';
import QuoteLineItemRow from './quoteLineItemRow';
import useQuoteLineItemMutations from '../../hooks/useQuoteLineItemMutations';
import { formatCurrency } from '../../utils/formatters';

// Totals block. Uses local line-item drafts for real-time subtotal
// computation, with discount and VAT from the server quote data.
function Totals({ quote, drafts = {} }) {
    const locked = quote?.convertedToJobId != null || quote?.status === 'Sent' || quote?.status === 'Accepted' || quote?.status === 'Rejected';
    const currency = quote?.customerCurrency;

    // Compute pre-discount from local drafts (real-time) or server data.
    const draftItems = Object.values(drafts);
    const preDiscount = draftItems.length > 0
        ? draftItems.reduce((s, d) => s + (Number(d.quantity) || 0) * (Number(d.unitPrice) || 0), 0)
        : (quote?.lineItems ?? []).reduce((s, i) => s + (Number(i.lineTotal) || 0), 0);

    // Apply discount client-side for instant feedback.
    const dt = quote?.discountType ?? 0;
    const dv = Number(quote?.discountValue ?? 0);
    let subtotal = preDiscount;
    let discountAmount = 0;
    if (dt === 1) {
        discountAmount = Math.min(dv, preDiscount);
        subtotal = preDiscount - discountAmount;
    } else if (dt === 2) {
        discountAmount = preDiscount * (dv / 100);
        subtotal = preDiscount - discountAmount;
    }

    const discountLabel =
        quote?.discountType === 2
            ? `Discount (${Number(quote?.discountValue ?? 0)}%)`
            : 'Discount';

    return (
        <div className="d-flex justify-content-end">
            <div className="w-100 w-sm-auto" style={{ minWidth: 'min(300px, 100%)' }}>
                <Row label="Subtotal" value={formatCurrency(preDiscount, currency)} />
                <Row
                    label={discountLabel}
                    value={
                        quote?.discountType === 0
                            ? '—'
                            : `-${formatCurrency(discountAmount, currency)}`
                    }
                />
                <Row
                    label={quote?.isVatInclusive ? 'VAT (20%)' : 'VAT'}
                    value={
                        quote?.isVatInclusive
                            ? formatCurrency(subtotal * 0.2, currency)
                            : <span className="text-muted">Not applicable</span>
                    }
                />
                <div className="border-top pt-2 mt-1">
                    <Row label={<strong>Total</strong>}
                         value={<strong>{formatCurrency(subtotal + (quote?.isVatInclusive ? subtotal * 0.2 : 0), currency)}</strong>} />
                </div>
                {locked && (
                    <div className="text-muted small mt-2">
                        Totals are frozen because the quote is converted.
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="d-flex justify-content-between py-1">
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

export default function QuoteLineItemsTable({ quote }) {
    const { create, update, remove } = useQuoteLineItemMutations(quote?.id);
    const locked = quote?.convertedToJobId != null || quote?.status === 'Sent' || quote?.status === 'Accepted' || quote?.status === 'Rejected';
    const [lineItemDrafts, setLineItemDrafts] = useState({});
    const handleDraftChange = useCallback((itemId, draft) => {
        setLineItemDrafts(prev => ({ ...prev, [itemId]: draft }));
    }, []);

    // Sync drafts from server data on SignalR refetch.
    useEffect(() => {
        if (!quote?.lineItems) return;
        const updated = {};
        for (const item of quote.lineItems) {
            updated[item.id] = item;
        }
        setLineItemDrafts(updated);
    }, [quote?.lineItems]);

    const handleAdd = () => {
        create.mutate({
            description:   '',
            quantity:      1,
            unitPrice:     0,
            estimatedCost: 0,
            costType:      'Labour',
        });
    };

    const rows = quote?.lineItems ?? [];

    return (
        <div className="surface">
            <div className="table-responsive">
                <Table size="sm" className="align-middle mb-0" style={{ minWidth: 660 }}>
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th className="text-nowrap">Qty</th>
                            <th className="text-nowrap">Unit Price</th>
                            <th className="text-nowrap">Est. Cost</th>
                            <th className="text-nowrap">Cost Type</th>
                            <th className="text-end text-nowrap">Line Total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-muted text-center py-3">
                                    No line items yet.
                                </td>
                            </tr>
                        )}
                        {rows.map(item => (
                            <QuoteLineItemRow
                                key={item.id}
                                item={item}
                                currency={quote?.customerCurrency}
                                onUpdate={({ id, body }) => update.mutate({ id, body })}
                                onRemove={(id) => remove.mutate(id)}
                                onDraftChange={handleDraftChange}
                                disabled={locked}
                            />
                        ))}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start pt-3 gap-3">
                <Button color="primary" size="sm" outline
                        onClick={handleAdd}
                        disabled={locked || create.isPending}
                        className="d-inline-flex align-items-center gap-1">
                    <BiPlus /> Add line item
                </Button>
                <Totals quote={quote} drafts={lineItemDrafts} />
            </div>
        </div>
    );
}
